import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, ExternalLink } from 'lucide-react';
import { buttonVariants } from '@/components/ui/button-variants';
import { Card, CardContent } from '@/components/ui/card';
import { Icon } from '@/components/ui/icon';
import type { SetupStep } from '../useSetupStatus';

/**
 * The two steps that genuinely cannot happen inside the wizard.
 *
 * This is not the escape hatch for anything awkward — floors, areas, tables,
 * branches, stations, items and staff are all done in place, using the very
 * dialogs their own screens use. Two are different in kind:
 *
 *  - **Publish a menu.** The menu builder is *itself* a four-step wizard that
 *    accumulates a draft and commits it at the end (`createMenuBody`, then
 *    `attachItemBodies`). Embedding it would put a second step strip inside this
 *    one, with its own Back, Next and Cancel competing for the same clicks — and
 *    it would import the exact "nothing lands until you finish" behaviour this
 *    wizard exists to remove.
 *  - **Draw the floor map.** The editor is a canvas: drag, snap, zoom, a
 *    tool palette and a selection model, sized to the floor's own width and
 *    height. There is no form to inline, and a canvas in a step body is a canvas
 *    nobody can draw on.
 *
 * So the step says so, in the UI, rather than pretending. Leaving is safe —
 * nothing here is a draft — and on return the probe re-reads the API, so the
 * step ticks itself without being told.
 */
export function ElsewhereStep({ step }: { step: SetupStep }) {
  const { t } = useTranslation();
  const screen = t(`setup.checklist.screens.${step.screen}`);

  return (
    <Card>
      <CardContent className="space-y-3 p-4">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 shrink-0 text-ink-muted">
            <Icon as={ExternalLink} aria-hidden />
          </span>
          <div className="min-w-0 space-y-1">
            <p className="text-sm font-medium">{t('setup.wizard.elsewhere.title')}</p>
            <p className="text-sm text-ink-muted">
              {/* Only `menu` and `map` reach this component, and both keys are
                  defined — no defaultValue, so the lint rule checks the
                  namespace instead of accepting an English fallback. */}
              {t(`setup.wizard.elsewhere.why.${step.id}`)}
            </p>
            <p className="text-sm text-ink-muted">{t('setup.wizard.elsewhere.returnHint')}</p>
          </div>
        </div>

        {step.to ? (
          <Link to={step.to} className={buttonVariants({ variant: 'default', size: 'sm' })}>
            {t('setup.wizard.elsewhere.open', { screen })}
            <Icon as={ArrowRight} aria-hidden />
          </Link>
        ) : (
          // No link rather than a broken one: the actor either lacks the write
          // key the API demands or would only reach /403 on that screen.
          <p className="text-sm text-ink-muted">{t('setup.checklist.noAccess', { screen })}</p>
        )}
      </CardContent>
    </Card>
  );
}
